import React, { useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { currentVesselSelector } from "../../model/slice/vesselSlice";
import { fetchVesselAsync } from "../../model/async/vesselAsync";
import { DashboardHeader } from "./header/header";
import { DashboardDisplayPage } from "./display/display";
import { DashboardEditPage } from "./edit/edit";
import { DashboardSettingsPage } from "./settings/settings";
import { Box, CircularProgress } from "@mui/material";
import { CreateDashboardPage } from "./createDashboard/createDashboard";
import { fetchChannelNameAndConfig } from "../../model/async/channelAsync";
import {
  fetchChannelLatestDataForDashboard,
  fetchChartsInfoForDashboards,
} from "../../model/async/customDashboardAsync";
import { getSortedDashboards } from "./dashboardUtil";
import { DASHBOARD_ITEM_TYPE, DASHBOARD_MODE } from "./constants";
import { DashboardComment } from "./comment/comment";
import { postDashboard } from "../../api/dashboard";
import { fetchAddon, fetchVesselApplicationByVesselId } from "../../api/addon";

export const Dashboard = React.memo(() => {
  const dispatch = useDispatch();

  const { vesselId, dashboardId } = useParams();
  const navigate = useNavigate();
  const currentVessel = useSelector(currentVesselSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDashboard, setSelectedDashboard] = useState();
  const [dashboards, setDashboards] = useState();
  const [mode, setMode] = useState(DASHBOARD_MODE.DISPLAY); //display, edit, settings, create
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [addComment, setAddComment] = useState(false);
  const [showAddCommentDlg, setShowAddCommentDlg] = useState(false);
  const [labelEdit, setLabelEdit] = useState({ flg: false });
  const [addCommentDlgPos, setAddCommentDlgPos] = useState({ x: 0, y: 0 });
  const [isLiveTrendAddonEnabled, setIsLiveTrendAddonEnabled] = useState(false);
  const element = useRef(null);
  const parentEl = useRef(null);

  const [labels, setLabels] = useState([]);

  const postDashboardUpdate = async (dashboard) => {
    await postDashboard(vesselId, dashboard, true);
  }

  useEffect(() => {
    setIsLoading(true);
    if (!vesselId) return;
    dispatch(fetchVesselAsync({ vesselId }));

    const fetchAddons = async () => {
      const addonId = (await fetchAddon('livetrend' ))[0]?.id;
      const isEnabled = await fetchVesselApplicationByVesselId(vesselId, addonId);
      setIsLiveTrendAddonEnabled(!!isEnabled?.length);
    }

    fetchAddons();
  }, [vesselId]);

  useEffect(() => {
    if (!currentVessel) return;
    const getDashboards = async () => {
      try {
        setIsLoading(true);
        const sortedDashboards = await getSortedDashboards(currentVessel.id);
        if (sortedDashboards.length > 0) {
          setDashboards(sortedDashboards);
          setMode(DASHBOARD_MODE.DISPLAY);
          if (dashboardId) {
            setSelectedDashboard(sortedDashboards.find((e) => e.id === dashboardId));
          } else {
            setMode(DASHBOARD_MODE.DISPLAY);
            setSelectedDashboard(sortedDashboards[0]);
            navigate(`/dashboard/${sortedDashboards[0].id}/${currentVessel.id}`, { replace: true });
          }
        } else {
          setDashboards([]);
          setSelectedDashboard(null);
          setMode(DASHBOARD_MODE.CREATE);
        }
      } catch (error) {
        setDashboards([]);
      } finally {
        setIsLoading(false);
      }
    };

    dispatch(fetchChannelNameAndConfig({ vesselId: currentVessel.id }));
    dispatch(fetchChannelLatestDataForDashboard({ vesselId: currentVessel.id }));
    dispatch(fetchChartsInfoForDashboards({ vesselId: currentVessel.id }));
    getDashboards();
  }, [JSON.stringify(currentVessel)]);

  useEffect(() => {
    setMode(DASHBOARD_MODE.DISPLAY);
    setIsDrawerOpen(false);
    setLabels([]);
    if (!dashboardId || !selectedDashboard?.id) return;
    setSelectedDashboard(dashboards.find((e) => e.id === dashboardId));
  }, [dashboardId]);

  useEffect(() => {
    if (!selectedDashboard || !element.current || isLoading) return;

    const lbls = selectedDashboard.items?.filter(item => item.type === DASHBOARD_ITEM_TYPE.LABEL);
    setLabels(lbls);
  }, [selectedDashboard, isLoading]);

  const capturedPointerLocation = (event) => {
    const bounds = element.current.getBoundingClientRect();
    setAddComment(false);
    setAddCommentDlgPos({ x: event.pageX - bounds.left, y: event.pageY - bounds.top });
    setShowAddCommentDlg(true);
  };

  const onLabelCreated = async (data) => {
    if (data.text !== "") {
      data.id = self.crypto.randomUUID();
      data.type = DASHBOARD_ITEM_TYPE.LABEL;
      setLabels([
        ...labels,
        data,
      ]);

      const dboard = selectedDashboard;
      dboard.items.push(data);
      await postDashboardUpdate(dboard);
    }
    setShowAddCommentDlg(false);
  };

  const onLabelDeleted = async (data) => {
    const tmp = labels.filter(p => p.id !== data.id)
    setLabels(tmp);

    const items = selectedDashboard.items.filter(i => i.id !== data.id);
    await postDashboardUpdate({ ...selectedDashboard, items });
    setDashboards((prev) => prev.map(d => {
      if (d.id === selectedDashboard.id) {
        return { ...d, items };
      }
      return d;
    }));
    setSelectedDashboard({ ...selectedDashboard, items });
  };

  const onLabelUpdated = async (data) => {
    if (labelEdit.flg && labelEdit.label.text !== data.text) {
      const tmp = labels.map((l) => {
        if (l.id === labelEdit.label.id) {
          l.text = data.text;
        }
        return l;
      });

      setLabels(tmp);

      const items = selectedDashboard.items.map((i) => {
        if (i.id === data.id) {
          i.text = data.text;
        }
        return i;
      });
      await postDashboardUpdate({ ...selectedDashboard, items });
    }
    setLabelEdit({ flg: false });
  };

  const onLabelPosUpdated = (data) => {
    const tmp = labels.map((l) => {
      if (l.id === data.id) {
        l.position = data.position;
      }
      return l;
    });
    setLabels(tmp);

    const items = selectedDashboard.items.map((i) => {
      if (i.id === data.id) {
        i.position = data.position;
      }
      return i;
    });
    postDashboardUpdate({ ...selectedDashboard, items });
  }

  const triggerEdit = (data) => {
    setLabelEdit({ flg: true, label: data });
  }

  const displayDashboardByMode = (mode) => {
    switch (mode) {
      case DASHBOARD_MODE.DISPLAY:
        return (
          <DashboardDisplayPage
            key={`display-${dashboardId}`}
            currentVessel={currentVessel}
            selectedDashboard={selectedDashboard}
            elRef={element}
          />
        );
      case DASHBOARD_MODE.EDIT:
        return (
          <DashboardEditPage
            key={`edit-${dashboardId}`}
            currentVessel={currentVessel}
            isDrawerOpen={isDrawerOpen}
            setIsDrawerOpen={setIsDrawerOpen}
            selectedDashboard={selectedDashboard}
            setSelectedDashboard={setSelectedDashboard}
            setDashboards={setDashboards}
            isLiveTrendAddonEnabled={isLiveTrendAddonEnabled}
          />
        );
      case DASHBOARD_MODE.SETTINGS:
        return (
          <DashboardSettingsPage
            key={`settings-${dashboardId}`}
            currentVessel={currentVessel}
            setMode={setMode}
            dashboards={dashboards}
            setDashboards={setDashboards}
            selectedDashboard={selectedDashboard}
            setSelectedDashboard={setSelectedDashboard}
          />
        );
      case DASHBOARD_MODE.CREATE:
        return (
          <CreateDashboardPage
            key={`create-${dashboardId}`}
            currentVessel={currentVessel}
            setMode={setMode}
            setDashboards={setDashboards}
            setSelectedDashboard={setSelectedDashboard}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      {!isLoading ? (
        <Box component={"div"}
          sx={{
            position: "relative",
            maxWidth: "calc(100dvw - 77px)",
            maxHeight: "calc(100dvh - 140px)",
            overflow: "auto",
          }}
        >
          {dashboards.length > 0  &&
            <DashboardHeader
              currentVessel={currentVessel}
              mode={mode}
              setMode={setMode}
              setIsDrawerOpen={setIsDrawerOpen}
              dashboards={dashboards}
              setDashboards={setDashboards}
              selectedDashboard={selectedDashboard}
              setSelectedDashboard={setSelectedDashboard}
              setAddComment={setAddComment}
            />
          }
          
          <Box
            ref={parentEl}
            sx={{ 
              padding: "0 24px", 
              ...mode === DASHBOARD_MODE.EDIT ? { 
                width: 'calc(100dvw - 450px)', 
                height: 'calc(100dvh - 140px)', 
                overflow: 'auto' 
              } : { 
                width: 'calc(100dvw - 92px)', 
                height: 'calc(100dvh - 140px)', 
              } 
            }}
          >
            {displayDashboardByMode(mode)}
          </Box>
          {addComment && (
            <Box component={"div"}
              sx={{
                position: "absolute",
                top: 0,
                width: "100%",
                height: "100%",
                background: "black",
                opacity: 0.4,
              }}
              onClick={capturedPointerLocation}
            >
            </Box>
          )}
          {mode == DASHBOARD_MODE.DISPLAY && labels?.map((label) => (
            <DashboardComment key={label.id} 
              position={label.position} 
              element={element.current}
              parentEl={parentEl.current}
              label={label} 
              isUpdate={false}
              onLabelDeleted={onLabelDeleted}
              onLabelPosUpdated={onLabelPosUpdated}
              triggerEdit={triggerEdit} />
          ))}
          {showAddCommentDlg && <DashboardComment position={addCommentDlgPos} element={element.current} onLabelCreated={onLabelCreated} isUpdate={true} />}
          {labelEdit.flg && <DashboardComment position={labelEdit.label.position} element={element.current} label={labelEdit.label} onLabelCreated={onLabelUpdated} isUpdate={true} />}
        </Box>
      ) : (
        <Box>
          <CircularProgress />
        </Box>
      )}
    </>
  );
});
